import * as React from "react"

import { cn } from "../../cn"
import { COVAL_MARK_PATHS } from "../../covalMark"
import {
  buildCymaticFigureReel,
  buildSofiaLogoFigure,
  CYMATIC_SCENE_MS,
  getCymaticParticleCount,
  getCymaticTransition,
  writeCymaticTargets,
} from "../../cymatic/cymaticFigures"

const PARTICLE_HIDE_DELAY_MS = 180
const SQUIRCLE_EXPONENT = 4.4
const SQUIRCLE_RADIUS = 0.84

/**
 * The stroked mark's tight bounding box, so the inset alone decides how much of
 * the tile the logo covers: `inset-[16%]` leaves 68%, matching the width and
 * height of the square Chladni plates the particle field draws (`SQUARE` in
 * cymaticFigures). Resting and animated therefore render the mark at the same
 * size.
 */
const MARK_VIEWBOX = 85.25

function getPrefersReducedMotion() {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  )
}

function subscribeToReducedMotion(onChange: () => void) {
  if (
    typeof window === "undefined" ||
    typeof window.matchMedia !== "function"
  ) {
    return () => undefined
  }

  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)")
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", onChange)
    return () => mediaQuery.removeEventListener("change", onChange)
  }

  mediaQuery.addListener(onChange)
  return () => mediaQuery.removeListener(onChange)
}

function getServerReducedMotionSnapshot() {
  return false
}

/**
 * A theme-aware particle field that continuously morphs through the Chladni
 * figures. Particle identity is stable across the reel, so every frame between
 * two figures is a coherent intermediate form. When animation stops, the same
 * pool quickly reforms into the complete mark.
 */
function CymaticCanvas({
  size,
  animated,
  onReady,
}: {
  size: number
  animated: boolean
  onReady: () => void
}) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null)
  const animatedRef = React.useRef(animated)
  const restartRef = React.useRef(false)

  React.useEffect(() => {
    if (animated && !animatedRef.current) {
      restartRef.current = true
    }
    animatedRef.current = animated
  }, [animated])

  React.useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) {
      return
    }
    const context = canvas.getContext("2d")
    if (!context) {
      return
    }

    let appliedDevicePixelRatio = 0
    const syncCanvasResolution = () => {
      const devicePixelRatio = window.devicePixelRatio || 1
      if (devicePixelRatio === appliedDevicePixelRatio) {
        return
      }
      canvas.width = Math.round(size * devicePixelRatio)
      canvas.height = Math.round(size * devicePixelRatio)
      context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0)
      appliedDevicePixelRatio = devicePixelRatio
    }
    syncCanvasResolution()

    let color = getComputedStyle(canvas).color || "currentColor"
    const readColor = () => {
      color = getComputedStyle(canvas).color || color
    }

    const drawRadius = size * 0.5
    const center = size / 2
    const particleRadius = Math.max(0.5, Math.min(1.2, size * 0.026))
    const count = getCymaticParticleCount(size)
    let figures = buildCymaticFigureReel(count, size)
    const logo = buildSofiaLogoFigure(count)

    const positionX = new Float32Array(count)
    const positionY = new Float32Array(count)
    const targetX = new Float32Array(count)
    const targetY = new Float32Array(count)

    writeCymaticTargets(
      logo.points,
      logo.points,
      { progress: 0, energy: 0 },
      targetX,
      targetY,
    )
    positionX.set(targetX)
    positionY.set(targetY)

    const confine = (x: number, y: number): [number, number] => {
      const distance =
        (Math.abs(x) ** SQUIRCLE_EXPONENT + Math.abs(y) ** SQUIRCLE_EXPONENT) **
          (1 / SQUIRCLE_EXPONENT) /
        SQUIRCLE_RADIUS
      return distance > 1 ? [x / distance, y / distance] : [x, y]
    }

    const step = (pull: number) => {
      for (let index = 0; index < count; index += 1) {
        const nextX =
          positionX[index] + (targetX[index] - positionX[index]) * pull
        const nextY =
          positionY[index] + (targetY[index] - positionY[index]) * pull
        ;[positionX[index], positionY[index]] = confine(nextX, nextY)
      }
    }

    const draw = (timestamp: number, energy: number) => {
      context.clearRect(0, 0, size, size)
      context.fillStyle = color
      context.globalAlpha = 1
      context.beginPath()
      const buzz = 0.0035 + energy * 0.008
      for (let index = 0; index < count; index += 1) {
        const phase = timestamp * 0.0022 + index * 1.618
        const x = positionX[index] + Math.sin(phase) * buzz
        const y = positionY[index] + Math.cos(phase * 0.87) * buzz
        const canvasX = center + x * drawRadius
        const canvasY = center + y * drawRadius
        context.moveTo(canvasX + particleRadius, canvasY)
        context.arc(canvasX, canvasY, particleRadius, 0, Math.PI * 2)
      }
      context.fill()
    }

    let animationFrame = 0
    let animationStart = 0
    let previousTimestamp = 0
    let isVisible = true
    let pausedAt = 0
    let hasDrawn = false
    const frame = (timestamp: number) => {
      if (!animationStart) {
        animationStart = timestamp
      }
      if (restartRef.current) {
        figures = buildCymaticFigureReel(
          count,
          size,
          Math.random,
          figures.map((figure) => figure.id),
        )
        animationStart = timestamp
        restartRef.current = false
      }
      let elapsed = timestamp - animationStart
      const isAnimated = animatedRef.current
      if (isAnimated && elapsed >= figures.length * CYMATIC_SCENE_MS) {
        figures = buildCymaticFigureReel(
          count,
          size,
          Math.random,
          figures.map((figure) => figure.id),
        )
        animationStart = timestamp
        elapsed = 0
      }
      const transition = isAnimated
        ? getCymaticTransition(elapsed, figures.length)
        : { fromIndex: 0, toIndex: 0, progress: 0, energy: 0 }

      // At rest the field holds the logo, which the reel itself never contains.
      writeCymaticTargets(
        isAnimated ? figures[transition.fromIndex].points : logo.points,
        isAnimated ? figures[transition.toIndex].points : logo.points,
        transition,
        targetX,
        targetY,
      )

      const delta = previousTimestamp
        ? Math.min(32, timestamp - previousTimestamp)
        : 1000 / 60
      const responseMs = isAnimated ? 86 : 38
      step(1 - Math.exp(-delta / responseMs))
      syncCanvasResolution()
      readColor()
      draw(timestamp, transition.energy)
      if (!hasDrawn) {
        hasDrawn = true
        onReady()
      }
      previousTimestamp = timestamp
      animationFrame = requestAnimationFrame(frame)
    }

    const visibilityObserver =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(([entry]) => {
            const nextVisible = entry?.isIntersecting ?? true
            if (nextVisible === isVisible) {
              return
            }
            isVisible = nextVisible
            if (!isVisible) {
              pausedAt = performance.now()
              cancelAnimationFrame(animationFrame)
              animationFrame = 0
              return
            }

            if (pausedAt && animationStart) {
              animationStart += performance.now() - pausedAt
            }
            pausedAt = 0
            previousTimestamp = 0
            animationFrame = requestAnimationFrame(frame)
          })

    visibilityObserver?.observe(canvas)
    animationFrame = requestAnimationFrame(frame)
    return () => {
      visibilityObserver?.disconnect()
      cancelAnimationFrame(animationFrame)
    }
  }, [onReady, size])

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="absolute inset-0"
      style={{ width: size, height: size }}
    />
  )
}

export interface CymaticFieldProps extends React.HTMLAttributes<HTMLSpanElement> {
  /** Rendered width and height in pixels. */
  size?: number
  /** Plays the thinking animation; at rest the field holds the static mark. */
  animated?: boolean
}

/**
 * The mark as a particle field. It rests as the static resonance-tile logo and
 * becomes a continuously morphing particle reel while animated. The reel adapts
 * its figure detail to the rendered size and never morphs through the logo
 * itself; the field only holds that shape at rest, so it reforms into the mark
 * before the canvas fades away. Reduced-motion users keep the exact static SVG
 * mark.
 */
const CymaticField = React.forwardRef<HTMLSpanElement, CymaticFieldProps>(
  ({ size = 28, animated = false, className, children, ...props }, ref) => {
    const [particlesVisible, setParticlesVisible] = React.useState(false)
    const [readySize, setReadySize] = React.useState<number | null>(null)
    const prefersReducedMotion = React.useSyncExternalStore(
      subscribeToReducedMotion,
      getPrefersReducedMotion,
      getServerReducedMotionSnapshot,
    )

    const shouldAnimate = animated && !prefersReducedMotion
    const handleParticlesReady = React.useCallback(() => {
      setReadySize(size)
    }, [size])

    React.useEffect(() => {
      if (prefersReducedMotion) {
        setParticlesVisible(false)
        setReadySize(null)
        return
      }

      if (animated) {
        setParticlesVisible(true)
        return
      }

      const hideTimer = window.setTimeout(() => {
        setParticlesVisible(false)
        setReadySize(null)
      }, PARTICLE_HIDE_DELAY_MS)

      return () => {
        window.clearTimeout(hideTimer)
      }
    }, [animated, prefersReducedMotion])

    const shouldShowParticles = shouldAnimate || particlesVisible
    const particleFieldVisible = shouldAnimate && readySize === size

    return (
      <span
        ref={ref}
        style={{ width: size, height: size }}
        className={cn(
          "relative inline-flex shrink-0 items-center justify-center overflow-hidden",
          className,
        )}
        {...props}
      >
        <svg
          aria-hidden="true"
          viewBox={`0 0 ${MARK_VIEWBOX} ${MARK_VIEWBOX}`}
          className={cn(
            "absolute inset-[16%] transition-opacity duration-300 ease-out",
            particleFieldVisible ? "opacity-0" : "opacity-100",
          )}
          style={{
            transitionDuration: particleFieldVisible ? "300ms" : "90ms",
          }}
          fill="none"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {COVAL_MARK_PATHS.map((path) => (
            <path key={path.d} strokeWidth={path.strokeWidth} d={path.d} />
          ))}
        </svg>

        {shouldShowParticles ? (
          <span
            aria-hidden="true"
            className={cn(
              "absolute inset-0 transition-opacity ease-out",
              particleFieldVisible ? "opacity-100" : "opacity-0",
            )}
            style={{
              transitionDuration: particleFieldVisible ? "420ms" : "140ms",
            }}
          >
            <CymaticCanvas
              key={size}
              size={size}
              animated={shouldAnimate}
              onReady={handleParticlesReady}
            />
          </span>
        ) : null}
        {children}
      </span>
    )
  },
)

CymaticField.displayName = "CymaticField"

export { CymaticField }
